import { mergeMap, tap, map, filter, debounceTime, buffer } from 'rxjs/operators'
import { Epic } from 'interfaces/middleware'
import { combineEpics } from 'redux-observable'
import { LOGOUT } from 'actions/authActions'
import { HANDLE_NETWORK_ERROR, HANDLE_UNAUTHORIZED_ERROR, PAGE_CHANGED } from 'actions/globalActions'
import { FORM_ONBOARDING_REGISTER, FORM_PASSWORD_RESET } from 'constants/forms'
import { isAuthenticatedSelector } from 'selectors/globalSelectors'
import { RELOAD } from 'actions/navigationActions'
import { canGoBackSelector } from 'selectors/navigationSelectors'
import { LINKAGE_RESULT, LinkageResultPayload } from 'actions/linkageActions'
import { Communication as Com } from 'tangle-shared'
import { of } from 'rxjs'
import { trackUIEventAction } from 'actions/analyticsActions'
import { byType } from 'rxOperators/byType'
import { end } from 'rxOperators/end'
import { formSubmitted } from 'rxOperators/formsLegacy'

const NETWORK_ERRORS_DEBOUNCE_TIME = 1000

const navigationEndEpic: Epic = (action$, store, { remote, document }) =>
  action$
    .pipe(byType(PAGE_CHANGED))
    .pipe(end(({ payload }) => {
      const state = store.getState()

      return remote.sendNavigationEndEvent(
        payload.next,
        !!isAuthenticatedSelector(store.getState()),
        canGoBackSelector(state, document.location)
      )
    }))

const registerEpic: Epic = (action$, _store, { remote }) =>
  action$
    .pipe(formSubmitted(FORM_ONBOARDING_REGISTER))
    .pipe(end((action) => {
      if (action.payload.formName !== FORM_ONBOARDING_REGISTER) {
        return
      }

      const { formValues } = action.payload
      if (!formValues) {
        return
      }

      const { email, password } = formValues
      remote.sendRegisterEvent({ email, password })
    }))

const passwordResetEpic: Epic = (action$, _store, { remote }) =>
  action$
    .pipe(formSubmitted(FORM_PASSWORD_RESET))
    .pipe(end((action) => {
      if (action.payload.formName !== FORM_PASSWORD_RESET) {
        return
      }

      const { formValues } = action.payload
      if (!formValues) {
        return
      }

      const { email, password } = formValues
      remote.sendPasswordResetEvent({ email, password })
    }))

const logoutOnErrorEpic: Epic = (action$, store, { remote }) =>
  action$
    .pipe(byType(HANDLE_UNAUTHORIZED_ERROR)).pipe(
      filter(() => {
        const state = store.getState()
        return isAuthenticatedSelector(state) !== false
      })
    )
    .pipe(end(remote.sendLogoutEvent))

const logoutEpic: Epic = (action$, _store, { remote }) =>
  action$
    .pipe(byType(LOGOUT))
    .pipe(end(remote.sendLogoutEvent))

const networkErrorEpic: Epic = (action$, _store, { remote }) => {
  const error$ = action$
    .pipe(byType(HANDLE_NETWORK_ERROR))

  const timer$ = error$.pipe(
    debounceTime(NETWORK_ERRORS_DEBOUNCE_TIME)
  )

  return error$.pipe(
    buffer(timer$)
  )
    .pipe(end(remote.sendNetworkErrorEvent))
}

const reloadEpic: Epic = (action$, _store, { remote }) =>
  action$
    .pipe(byType(RELOAD))
    .pipe(end(({ payload }) => remote.sendReloadEvent(payload)))


export const getEventsForLinkageResult = (payload: LinkageResultPayload): Com.KochavaEvent[] => {
  const { all, new: fresh } = payload
  const events: Com.KochavaEvent[] = []

  let otherLinked = false
  if (fresh === 'credit_profile') {
    events.push({ name: 'Credit_Linkage_Completed', data: {}})
    if (all.includes('first_account')) {
      otherLinked = true
    }
  }

  if (fresh === 'first_account') {
    events.push({ name: '1st_Account_Linkage_Completed', data: {}})
    if (all.includes('credit_profile')) {
      otherLinked = true
    }
  }

  events.push(
    otherLinked
      ? { name: 'Credit_And_1st_Account_Linkage_Completed', data: {}}
      : { name: 'Credit_Or_1st_Account_Linkage_Completed', data: {}}
  )

  return events
}

const linkageResultEpic: Epic = (action$, _store, { remote }) =>
  action$
    .pipe(byType(LINKAGE_RESULT)).pipe(
      map(({ payload }) => getEventsForLinkageResult(payload)),
      tap((events) => {
        events.forEach((event) => {
          remote.sendAnalyticsEvent('kochava', event)
        })
      }),
      map((events) => events.map((event) => {
        return trackUIEventAction({
          category: 'misc',
          action: event.name,
        })
      })),
      mergeMap((actions) => of(...actions))
    )

export default combineEpics(
  navigationEndEpic,
  registerEpic,
  passwordResetEpic,
  logoutOnErrorEpic,
  logoutEpic,
  networkErrorEpic,
  reloadEpic,
  linkageResultEpic,
)
